import { 
  MODULE_SECRETS, 
  MODULE_AUDIT_LOGS, 
  GATEWAY_MODULES 
} from '../../../../models/modules.ts';

export class OrgBillingTab extends HTMLElement {
  private _subscription: any = null;
  private _invoices: any[] = [];
  private _usage: number = 0;
  private _mauUsage: number = 0;
  private _paymentMethod: any = null;
  private _availableModules: any[] = [];
  private _plans: any[] = [];
  
  // Pending State
  private _pendingPlanId: string | null = null;
  private _pendingModuleCounts: Map<string, number> = new Map();
  private _originalModuleCounts: Map<string, number> = new Map();

  set subscription(val: any) {
    this._subscription = val;
    this.syncOriginalState();
    this.render();
  }

  set invoices(val: any[]) {
    this._invoices = val || [];
    this.render();
  }

  set usage(val: number) {
    this._usage = val || 0;
    this.render();
  }

  set mauUsage(val: number) {
    this._mauUsage = val || 0;
    this.render();
  }

  set paymentMethod(val: any) {
    this._paymentMethod = val;
    this.render();
  }

  set availableModules(val: any[]) {
    this._availableModules = val || [];
    this.render();
  }

  set plans(val: any[]) {
    this._plans = val || [];
    this.render();
  }

  private syncOriginalState() {
    if (!this._subscription) return;
    this._originalModuleCounts.clear();
    this._pendingModuleCounts.clear();
    
    const modules = this._subscription.active_modules || this._subscription.modules || [];
    const cancelingIds = [...(this._subscription.canceling_module_ids || [])];
    modules.forEach((m: any) => {
      const id = typeof m === 'string' ? m : m.id;
      const cancelIdx = cancelingIds.indexOf(id);
      if (cancelIdx !== -1) {
        cancelingIds.splice(cancelIdx, 1);
        return;
      }
      this._originalModuleCounts.set(id, (this._originalModuleCounts.get(id) || 0) + 1);
      this._pendingModuleCounts.set(id, (this._pendingModuleCounts.get(id) || 0) + 1);
    });
    this._pendingPlanId = this._subscription.plan_id || this._subscription.plan?.id || null;
  }

  public getChanges() {
    const { recurringDeltaCents, isAnnual } = this.calculateTotals();
    const moduleIds: string[] = [];
    this._pendingModuleCounts.forEach((count, id) => {
      for (let i = 0; i < count; i++) {
        moduleIds.push(id);
      }
    });
    return {
      planId: this._pendingPlanId,
      moduleIds,
      recurringDeltaCents,
      interval: isAnnual ? 'year' : 'month'
    };
  }

  private calculateTotals() {
    const limits = this.resolveLimits(this._pendingPlanId, this._pendingModuleCounts);
    const selectedPlan = this._plans.find(p => p.id === this._pendingPlanId) || this._subscription?.plan;
    const isAnnual = selectedPlan?.interval === 'year';
    
    // Calculate estimates
    let subtotalCents = selectedPlan?.price_cents || 0;
    const includedModules = selectedPlan?.modules || [];
    
    this._pendingModuleCounts.forEach((count, id) => {
      if (includedModules.includes(id)) return;
      const m = this._availableModules.find(am => am.id === id);
      if (m) {
        subtotalCents += (isAnnual ? m.price_cents * 12 : m.price_cents) * count;
      }
    });
    
    // Calculate current recurring
    const currentPlanId = this._subscription?.plan_id || this._subscription?.plan?.id;
    const currentPlan = this._plans.find(p => p.id === currentPlanId) || this._subscription?.plan;
    
    let currentRecurringCents = currentPlan?.price_cents || 0;
    const currentIncludedModules = currentPlan?.modules || [];
    const currentModules = this._subscription?.active_modules || this._subscription?.modules || [];
    
    currentModules.forEach((m: any) => {
      const id = typeof m === 'string' ? m : m.id;
      if (currentIncludedModules.includes(id)) return;
      const am = this._availableModules.find(a => a.id === id);
      if (am) {
        currentRecurringCents += currentPlan?.interval === 'year' ? am.price_cents * 12 : am.price_cents;
      }
    });

    const pendingRecurringCents = subtotalCents;
    const recurringDeltaCents = pendingRecurringCents - currentRecurringCents;

    const allowanceGB = limits.data_allowance_gb || 0;
    const usageGB = (this._usage || 0) / 1e9;
    const overageGB = Math.max(0, usageGB - allowanceGB);
    const dataOverageEstimateCents = overageGB * (limits.overage_rate_cents || 0);

    const mauAllowance = limits.mau_allowance || 0;
    const mauUsage = this._mauUsage || 0;
    const mauOverage = Math.max(0, mauUsage - mauAllowance);
    const mauOverageEstimateCents = mauOverage * (limits.mau_overage_rate_cents || 0);

    const overageEstimateCents = dataOverageEstimateCents + mauOverageEstimateCents;
    const totalEstimateCents = subtotalCents + overageEstimateCents;

    return {
      selectedPlan,
      isAnnual,
      subtotalCents,
      currentRecurringCents,
      pendingRecurringCents,
      recurringDeltaCents,
      allowanceGB,
      usageGB,
      dataOverageEstimateCents,
      mauAllowance,
      mauUsage,
      mauOverageEstimateCents,
      overageEstimateCents,
      totalEstimateCents,
      limits
    };
  }

  private hasChanges(): boolean {
    if (!this._subscription) return false;
    
    if (this._pendingPlanId !== (this._subscription.plan?.id || null)) return true;
    
    if (this._pendingModuleCounts.size !== this._originalModuleCounts.size) return true;
    let changed = false;
    this._pendingModuleCounts.forEach((count, id) => {
      if ((this._originalModuleCounts.get(id) || 0) !== count) changed = true;
    });
    this._originalModuleCounts.forEach((count, id) => {
      if ((this._pendingModuleCounts.get(id) || 0) !== count) changed = true;
    });
    return changed;
  }

  connectedCallback() {
    this.render();
  }

  private formatCurrency(cents: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private parseDate(val: any): string {
    if (!val) return 'N/A';
    const date = new Date(typeof val === 'number' ? val * 1000 : val);
    return date.toLocaleDateString();
  }

  private getFormattedEndDate(): string {
    if (this._subscription?.current_period_end) {
      return this.parseDate(this._subscription.current_period_end);
    }
    // Fallback: Last day of the current month
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return lastDay.toLocaleDateString();
  }

  private resolveLimits(planId: string | null, moduleCounts: Map<string, number>) {
    const limits = {
      data_allowance_gb: 0,
      overage_rate_cents: 0,
      config_history_limit: 0,
      mau_allowance: 0,
      mau_overage_rate_cents: 0
    };

    moduleCounts.forEach((count, id) => {
      if (count <= 0) return;
      const m = this._availableModules.find(am => am.id === id);
      if (!m) return;
      if (m.data_allowance_gb > limits.data_allowance_gb) limits.data_allowance_gb = m.data_allowance_gb;
      if (m.overage_rate_cents > 0) {
        if (limits.overage_rate_cents === 0 || m.overage_rate_cents < limits.overage_rate_cents) {
          limits.overage_rate_cents = m.overage_rate_cents;
        }
      }
      if (m.mau_allowance > limits.mau_allowance) limits.mau_allowance = m.mau_allowance;
      if (m.mau_overage_rate_cents > 0) {
        if (limits.mau_overage_rate_cents === 0 || m.mau_overage_rate_cents < limits.mau_overage_rate_cents) {
          limits.mau_overage_rate_cents = m.mau_overage_rate_cents;
        }
      }
      if (m.config_history_limit === 0 || (limits.config_history_limit !== 0 && m.config_history_limit > limits.config_history_limit)) {
        limits.config_history_limit = m.config_history_limit;
      }
    });

    return limits;
  }

  private render() {
    if (!this._subscription) {
      this.innerHTML = `<div style="padding: 40px; text-align: center; color: var(--text-dim); font-family: var(--font-mono);">LOADING_BILLING_STATE...</div>`;
      return;
    }

    const {
      selectedPlan,
      isAnnual,
      currentRecurringCents,
      pendingRecurringCents,
      recurringDeltaCents,
      allowanceGB,
      usageGB,
      mauAllowance,
      mauUsage,
      overageEstimateCents,
      totalEstimateCents,
      limits
    } = this.calculateTotals();

    const includedModules = selectedPlan?.modules || [];

    this.innerHTML = `
      <style>
        .billing-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          animation: fadeIn 0.4s ease-out;
          align-items: start;
        }
        @media (max-width: 1000px) {
          .billing-layout { grid-template-columns: 1fr; }
        }

        .section-label {
          font-family: var(--font-sketch);
          color: var(--text-dim);
          font-size: 0.9rem;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        /* Usage Card */
        .usage-progress {
          height: 8px;
          background: rgba(255,255,255,0.05);
          border-radius: 4px;
          margin: 16px 0;
          overflow: hidden;
          position: relative;
        }
        .usage-bar {
          height: 100%;
          background: var(--accent);
          transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Credit Card Vis */
        .credit-card {
          width: 100%;
          max-width: 260px;
          aspect-ratio: 1.58;
          background: linear-gradient(135deg, #1e1e1e 0%, #111111 100%);
          border-radius: 12px;
          padding: 16px;
          position: relative;
          color: white;
          box-shadow: 0 12px 24px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.1);
          overflow: hidden;
        }
        .credit-card::before {
          content: "";
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.03) 0%, transparent 60%);
          pointer-events: none;
        }
        .card-chip {
          width: 32px;
          height: 24px;
          background: linear-gradient(135deg, #ffd700 0%, #daa520 100%);
          border-radius: 4px;
          margin-bottom: 16px;
          position: relative;
          box-shadow: inset 0 0 4px rgba(0,0,0,0.2);
        }
        .card-number {
          font-family: var(--font-mono);
          font-size: 1rem;
          letter-spacing: 2px;
          margin-bottom: 16px;
          text-shadow: 0 2px 4px rgba(0,0,0,0.5);
        }

        /* Module Grid */
        .module-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 16px;
        }
        .module-item {
          padding: 16px;
          background: rgba(255,255,255,0.02);
          border: 1px solid var(--border-sketch);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .module-item:hover {
          background: rgba(var(--accent-rgb), 0.05);
          transform: translateY(-2px);
        }
        .module-item.active {
          border-color: var(--accent);
          background: rgba(var(--accent-rgb), 0.03);
        }
        .module-item.pending-add { border-style: dashed; border-color: #00ff00; }
        .module-item.pending-remove { border-style: dashed; border-color: #ff4444; opacity: 0.6; }

        /* Invoice Table */
        .invoice-table {
          width: 100%;
          border-collapse: collapse;
          font-family: var(--font-mono);
          font-size: 0.8rem;
        }
        .invoice-table th { text-align: left; padding: 8px; color: var(--text-dim); border-bottom: 1px solid var(--border-sketch); }
        .invoice-table td { padding: 8px; border-bottom: 1px solid rgba(255,255,255,0.03); }

        .toggle-container {
          display: inline-flex;
          background: rgba(255,255,255,0.05);
          padding: 4px;
          border-radius: 8px;
        }
        .toggle-btn {
          padding: 6px 16px;
          border-radius: 6px;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: bold;
        }
        .toggle-btn.active {
          background: var(--accent);
          color: white;
        }

        .right-col {
          display: flex;
          flex-direction: column;
          gap: 24px;
          min-width: 0;
        }

        .history-section {
          margin-top: 40px;
          grid-column: 1 / -1;
        }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      </style>

      <div class="billing-layout">
        <div class="left-col" style="display: flex; flex-direction: column; gap: 32px; min-width: 0;">
          
          <!-- Plan Selection -->
          <div class="sketchy-card">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
              <div class="section-label" style="margin: 0;">Subscription Plan</div>
              <div class="toggle-container">
                <div class="toggle-btn ${!isAnnual ? 'active' : ''}" data-interval="month">Monthly</div>
                <div class="toggle-btn ${isAnnual ? 'active' : ''}" data-interval="year">Annual</div>
              </div>
            </div>
            <div style="padding: 16px; background: rgba(245, 158, 11, 0.05); border: 1px solid var(--accent); border-radius: 12px; display: flex; justify-content: space-between; align-items: center;">
              <div>
                <div style="font-weight: bold; font-size: 1.1rem; color: var(--text-main);">${selectedPlan?.name || 'Pro Plan'}</div>
                <div style="font-size: 0.85rem; color: var(--text-dim); margin-top: 4px;">Standard enterprise-grade features</div>
              </div>
              <div style="text-align: right;">
                <div style="font-size: 1.5rem; font-weight: bold; font-family: var(--font-sketch); color: var(--accent);">
                  ${this.formatCurrency(selectedPlan?.price_cents || 0)}
                </div>
                <div style="font-size: 0.75rem; color: var(--text-dim); font-family: var(--font-mono);">${isAnnual ? 'per year' : 'per month'}</div>
              </div>
            </div>
          </div>

          <!-- Modules -->
          <div class="sketchy-card">
            <div class="section-label">Modules & Add-ons</div>
            <div class="module-grid">
              ${this._availableModules.map(m => {
                const originalCount = this._originalModuleCounts.get(m.id) || 0;
                const pendingCount = this._pendingModuleCounts.get(m.id) || 0;
                const isIncluded = includedModules.includes(m.id);
                
                let stateClass = '';
                const hasPendingChange = pendingCount !== originalCount;
                if (isIncluded) stateClass = 'active';
                else if (originalCount > 0 && pendingCount === 0) stateClass = 'pending-remove';
                else if (originalCount === 0 && pendingCount > 0) stateClass = 'pending-add';
                else if (originalCount > 0 && hasPendingChange) stateClass = pendingCount > originalCount ? 'pending-add' : 'pending-remove';
                else if (originalCount > 0) stateClass = 'active';

                const isMultiQty = ['byoc_gateway', 'dedicated_gateway'].includes(m.id);
                const cancelingCount = (this._subscription?.canceling_module_ids || []).filter((id: string) => id === m.id).length;

                return `
                  <div class="module-item ${stateClass}" data-id="${m.id}">
                    <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                      <app-icon name="${m.id === 'secrets' ? 'key' : m.id === 'audit_logs' ? 'activity' : 'cpu'}" size="20" style="color: ${pendingCount > 0 || isIncluded ? 'var(--accent)' : 'var(--text-dim)'};"></app-icon>
                      ${isIncluded ? '<span style="font-size: 0.6rem; color: var(--accent); font-weight: bold;">INCLUDED</span>' : ''}
                    </div>
                    <div>
                      <div style="font-weight: bold; font-size: 0.9rem; color: var(--text-main);">${m.name}</div>
                      <div style="font-size: 0.7rem; color: var(--text-dim); line-height: 1.3; margin-top: 4px;">${m.description}</div>
                    </div>
                    ${cancelingCount > 0 ? `
                      <div style="font-size: 0.65rem; color: #ff4444; margin-top: 4px; padding: 2px 6px; background: rgba(255,68,68,0.1); border-radius: 4px; display: inline-block;">
                        ${cancelingCount} expiring ${this.parseDate(this._subscription?.current_period_end)}
                      </div>
                    ` : ''}
                    <div style="margin-top: auto; display: flex; justify-content: space-between; align-items: flex-end;">
                      <div style="font-family: var(--font-mono); font-size: 0.75rem; color: var(--text-dim);">
                        ${this.formatCurrency(isAnnual ? m.price_cents * 12 : m.price_cents)}/${isAnnual ? 'yr' : 'mo'}
                      </div>
                      ${isMultiQty ? `
                        <div class="qty-controls" style="display: flex; align-items: center; gap: 8px;">
                          <app-button variant="ghost" size="xs" icon="minus" class="qty-minus" data-id="${m.id}"></app-button>
                          <span style="font-weight: bold; color: var(--text-main);">${pendingCount}</span>
                          <app-button variant="ghost" size="xs" icon="plus" class="qty-plus" data-id="${m.id}"></app-button>
                        </div>
                      ` : `
                        ${!isIncluded ? `
                          <app-icon name="${pendingCount > 0 ? 'minus-circle' : 'plus-circle'}" size="16" style="color: ${pendingCount > 0 ? '#ff4444' : 'var(--accent)'};"></app-icon>
                        ` : ''}
                      `}
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>

        </div>

        <div class="right-col">
          
          <!-- Usage & Summary -->
          <div class="sketchy-card">
            <div class="section-label">Order Summary</div>
            
            <div style="margin-bottom: 24px;">
              <div style="font-size: 2rem; font-weight: bold; font-family: var(--font-sketch); color: var(--text-main);">
                ${this.formatCurrency(totalEstimateCents)}
                <span style="font-size: 0.9rem; color: var(--text-dim); font-family: var(--font-mono);">USD</span>
              </div>
              <div style="font-size: 0.8rem; color: var(--text-dim); margin-top: 4px;">
                Estimated ${isAnnual ? 'annual' : 'monthly'} total
              </div>
            </div>

            <div style="display: flex; flex-direction: column; gap: 10px; font-size: 0.85rem; font-family: var(--font-mono);">
              <div style="display: flex; justify-content: space-between;">
                <span style="color: var(--text-dim);">Current Plan Cost</span>
                <span>${this.formatCurrency(currentRecurringCents)}</span>
              </div>
              ${recurringDeltaCents !== 0 ? `
              <div style="display: flex; justify-content: space-between;">
                <span style="color: var(--text-dim);">Recurring Change</span>
                <span style="color: ${recurringDeltaCents >= 0 ? 'var(--accent)' : '#10b981'};">
                  ${recurringDeltaCents >= 0 ? '+' : ''}${this.formatCurrency(recurringDeltaCents)}
                </span>
              </div>
              <div style="display: flex; justify-content: space-between; border-top: 1px solid var(--border-sketch); padding-top: 10px; margin-top: 4px;">
                <span style="color: var(--text-dim);">New Total</span>
                <span style="font-weight: bold; color: var(--text-main);">${this.formatCurrency(pendingRecurringCents)}</span>
              </div>
              ` : ''}
              ${overageEstimateCents > 0 ? `
                <div style="display: flex; justify-content: space-between; opacity: 0.7;">
                  <span style="color: var(--text-dim);">Est. Overage</span>
                  <span>${this.formatCurrency(overageEstimateCents)}</span>
                </div>
              ` : ''}
            </div>

            <div style="margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--border-sketch);">
              <div style="display: flex; justify-content: space-between; font-size: 0.8rem; margin-bottom: 8px;">
                <span style="color: var(--text-dim);">Data Usage</span>
                <span>${this.formatBytes(this._usage)} / ${allowanceGB} GB</span>
              </div>
              <div class="usage-progress">
                <div class="usage-bar" style="width: ${Math.min(100, (usageGB / (allowanceGB || 1)) * 100)}%;"></div>
              </div>
              <div style="font-size: 0.7rem; color: var(--text-dim); text-align: right;">
                Next reset: ${this.parseDate(this._subscription?.current_period_end)}
              </div>
            </div>

            ${limits.mau_allowance > 0 || mauUsage > 0 ? `
            <div style="margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--border-sketch);">
              <div style="display: flex; justify-content: space-between; font-size: 0.8rem; margin-bottom: 8px;">
                <span style="color: var(--text-dim);">Identity MAU Usage</span>
                <span>${mauUsage.toLocaleString()} / ${mauAllowance > 0 ? mauAllowance.toLocaleString() : '??'}</span>
              </div>
              <div class="usage-progress">
                <div class="usage-bar" style="width: ${mauAllowance > 0 ? Math.min(100, (mauUsage / mauAllowance) * 100) : 0}%;"></div>
              </div>
            </div>
            ` : ''}
          </div>

          <!-- Payment Method -->
          <div class="sketchy-card">
            <div class="section-label">Payment Method</div>
            <div style="display: flex; flex-direction: column; gap: 20px;">
              <div class="credit-card">
                <div class="card-chip"></div>
                <div class="card-number">
                  ${this._paymentMethod ? `•••• •••• •••• ${this._paymentMethod.last4}` : 'NO CARD'}
                </div>
                <div style="display: flex; justify-content: space-between; align-items: flex-end;">
                  <div>
                    <div style="font-size: 0.5rem; text-transform: uppercase; opacity: 0.6; margin-bottom: 2px;">Holder</div>
                    <div style="font-size: 0.75rem; text-transform: uppercase;">AUTH USER</div>
                  </div>
                  <div style="text-align: right;">
                    <div style="font-size: 0.5rem; text-transform: uppercase; opacity: 0.6; margin-bottom: 2px;">Exp</div>
                    <div style="font-size: 0.75rem;">${this._paymentMethod ? `${this._paymentMethod.exp_month}/${this._paymentMethod.exp_year}` : '--/--'}</div>
                  </div>
                </div>
                <div style="position: absolute; bottom: 12px; right: 16px; font-weight: 900; font-style: italic; font-size: 1.1rem; opacity: 0.6;">
                  ${this._paymentMethod?.brand?.toUpperCase() || ''}
                </div>
              </div>
              
              <div style="display: flex; flex-direction: column; gap: 12px;">
                    <app-button id="update-payment-btn" variant="secondary" size="sm">
                        Update card
                    </app-button>
                ${!this._subscription?.cancel_at_period_end ? `
                  <app-button variant="ghost" id="cancel-sub-btn" style="width: 100%; color: #ff4444;" size="sm">Cancel</app-button>
                ` : `
                  <div style="padding: 10px; background: rgba(255,68,68,0.05); border: 1px solid rgba(255,68,68,0.2); border-radius: 8px; font-size: 0.75rem; color: #ff4444; text-align: center;">
                    Ends on ${this.getFormattedEndDate()}
                  </div>
                `}
              </div>
            </div>
          </div>

        </div> <!-- End right-col -->

        <!-- Invoices (Full Width) -->
        <div class="history-section sketchy-card">
          <div class="section-label">Billing History</div>
          <div>
            <table class="invoice-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th style="width: 32px;"></th>
                </tr>
              </thead>
              <tbody>
                ${this._invoices.length > 0 ? this._invoices.map(inv => `
                  <tr>
                    <td>${this.parseDate(inv.created_at)}</td>
                    <td>${this.formatCurrency(inv.amount_paid)}</td>
                    <td>
                      <span style="color: ${inv.status === 'paid' ? '#00ff00' : 'orange'}; text-transform: uppercase; font-size: 0.6rem; font-weight: bold;">
                        ${inv.status}
                      </span>
                    </td>
                    <td>
                      <app-button variant="ghost" class="download-invoice-btn" data-id="${inv.id}" icon="download" size="xs"></app-button>
                    </td>
                  </tr>
                `).join('') : `
                  <tr><td colspan="4" style="text-align: center; padding: 20px; color: var(--text-dim);">No history available</td></tr>
                `}
              </tbody>
            </table>
          </div>
        </div>
      </div> <!-- End billing-layout -->
    `;

    this.setupListeners();
  }

  private setupListeners() {
    this.querySelectorAll('.module-item').forEach(el => {
      el.addEventListener('click', (e) => {
        // Prevent click if we clicked inside qty controls
        if ((e.target as HTMLElement).closest('.qty-controls')) return;

        const id = (el as HTMLElement).dataset.id;
        if (!id) return;
        if (['byoc_gateway', 'dedicated_gateway'].includes(id)) return;
        
        // Check if it's already included in the plan
        const selectedPlan = this._plans.find(p => p.id === this._pendingPlanId) || this._subscription.plan;
        if (selectedPlan?.modules?.includes(id)) return;

        // Exclusivity for gateways
        if (GATEWAY_MODULES.includes(id)) {
          GATEWAY_MODULES.forEach((gid: string) => {
            if (gid !== id) this._pendingModuleCounts.delete(gid);
          });
        }

        if ((this._pendingModuleCounts.get(id) || 0) > 0) {
          this._pendingModuleCounts.delete(id);
        } else {
          this._pendingModuleCounts.set(id, 1);
        }
        
        this.notifyChange();
        this.render();
      });
    });

    this.querySelectorAll('.qty-plus').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (e.currentTarget as HTMLElement).dataset.id;
        if (id) {
           this._pendingModuleCounts.set(id, (this._pendingModuleCounts.get(id) || 0) + 1);
           this.notifyChange();
           this.render();
        }
      });
    });

    this.querySelectorAll('.qty-minus').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (e.currentTarget as HTMLElement).dataset.id;
        if (id) {
           const current = this._pendingModuleCounts.get(id) || 0;
           if (current > 0) {
             if (current - 1 === 0) {
               this._pendingModuleCounts.delete(id);
             } else {
               this._pendingModuleCounts.set(id, current - 1);
             }
             this.notifyChange();
             this.render();
           }
        }
      });
    });

    this.querySelectorAll('.toggle-btn').forEach(el => {
      el.addEventListener('click', () => {
        const interval = (el as HTMLElement).dataset.interval;
        const plan = this._plans.find(p => p.interval === interval);
        if (plan) {
          this._pendingPlanId = plan.id;
          this.notifyChange();
          this.render();
        }
      });
    });

    this.querySelector('#update-payment-btn')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('update-payment', { bubbles: true, composed: true }));
    });

    this.querySelector('#cancel-sub-btn')?.addEventListener('click', async () => {
      this.dispatchEvent(new CustomEvent('cancel-subscription', { bubbles: true, composed: true }));
    });

    this.querySelectorAll('.download-invoice-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).dataset.id;
        if (id) {
          this.dispatchEvent(new CustomEvent('download-invoice', { 
            bubbles: true, 
            composed: true,
            detail: { invoiceId: id }
          }));
        }
      });
    });
  }

  private notifyChange() {
    this.dispatchEvent(new CustomEvent('change', { bubbles: true, composed: true }));
  }
}

if (!customElements.get('org-billing-tab')) {
  customElements.define('org-billing-tab', OrgBillingTab);
}
